/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Popover, Radio } from '@coursera/cds-core';
import { FilterIcon } from '@coursera/cds-icons';

import FilterPill from 'bundles/enterprise-ui/components/FilterPill';

import _t from 'i18n!nls/enterprise-ui';

export type SortSingleSelectOption<T extends string> = {
  /** The value reported via {@link SortSingleSelectProps.onChange} when this option is applied. */
  value: T;
  /** Human-readable text shown for this option in the radio list. */
  label: string;
};

export type SortSingleSelectProps<T extends string> = {
  /** The options rendered as single-select radios inside the popover. */
  options: ReadonlyArray<SortSingleSelectOption<T>>;
  /** The currently applied value. */
  value: T;
  /** Invoked with the chosen value when the user presses Apply (or Clear all). */
  onChange: (value: T) => void;
  /**
   * Trigger label.
   * @defaultValue "Sort by"
   */
  label?: string;
  /**
   * Accessible label for the trigger button.
   * @defaultValue {@link SortSingleSelectProps.label}
   */
  'aria-label'?: string;
  /**
   * Value applied when the user presses "Clear all".
   * @defaultValue the first option's value
   */
  defaultValue?: T;
  /**
   * Leading icon shown in the trigger pill.
   * @defaultValue `<FilterIcon />`
   */
  leadingIcon?: React.ReactNode;
  /**
   * When true, hides the leading icon from the trigger pill.
   * @defaultValue false
   */
  hideIcon?: boolean;
  'data-testid'?: string;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-25);
    min-width: 300px;

    /* Matches CDS MultiSelect's searchable listbox height so a long, dynamic option list
       (e.g. an org's admin list) scrolls internally instead of pushing the footer out of view. */
    max-height: 312px;
    overflow-y: auto;
  `,
  option: css`
    padding: 0 var(--cds-spacing-50);
    border-radius: var(--cds-border-radius-50);

    label {
      margin: 0;
    }

    &:hover {
      background: var(--cds-color-interactive-background-primary-hover-weak);
    }
  `,
  footer: css`
    display: flex;
    gap: var(--cds-spacing-200);
  `,
};

/**
 * A generic "sort by" control: a {@link FilterPill} trigger that opens a popover with a
 * single-select radio list and an Apply / Clear all footer. Selection is deferred — nothing
 * is reported until the user presses Apply (or Clear all, which reverts to
 * {@link SortSingleSelectProps.defaultValue}).
 *
 * Domain-specific value mapping (e.g. `{ key, direction }` sort descriptors) stays in the
 * caller; this component only deals in opaque string values.
 *
 * @example
 * ```tsx
 * <SortSingleSelect
 *   options={[
 *     { value: 'newest', label: _t('Newest') },
 *     { value: 'oldest', label: _t('Oldest') },
 *   ]}
 *   value={value}
 *   onChange={setValue}
 *   defaultValue="newest"
 * />
 * ```
 */
export function SortSingleSelect<T extends string>({
  options,
  value,
  onChange,
  label,
  'aria-label': ariaLabel,
  defaultValue,
  leadingIcon,
  hideIcon,
  'data-testid': dataTestId,
}: SortSingleSelectProps<T>) {
  const [anchor, setAnchor] = React.useState<HTMLButtonElement | null>(null);
  const [draft, setDraft] = React.useState<T>(value);
  const radioGroupName = React.useId();

  const triggerLabel = label ?? _t('Sort by');
  const clearValue: T | undefined = defaultValue ?? options[0]?.value;

  const getLeadingIcon = () => (hideIcon ? null : (leadingIcon ?? <FilterIcon />));

  const open = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDraft(value);
    setAnchor(event.currentTarget);
  };
  const close = () => setAnchor(null);

  return (
    <React.Fragment>
      <FilterPill
        label={triggerLabel}
        aria-label={ariaLabel ?? triggerLabel}
        leadingIcon={getLeadingIcon()}
        open={Boolean(anchor)}
        onClick={open}
        data-testid={dataTestId}
      />
      <Popover open={Boolean(anchor)} anchorElement={anchor} onClose={close}>
        <Popover.Body component="div" css={styles.body}>
          {options.map((option) => (
            <div css={styles.option} key={option.value}>
              <Radio
                name={radioGroupName}
                label={option.label}
                value={option.value}
                checked={draft === option.value}
                onChange={() => setDraft(option.value)}
              />
            </div>
          ))}
        </Popover.Body>
        <Popover.Footer css={styles.footer} role="toolbar" aria-label={triggerLabel}>
          <Button
            size="small"
            variant="primary"
            onClick={() => {
              onChange(draft);
              close();
            }}
          >
            {_t('Apply')}
          </Button>
          <Button
            size="small"
            variant="ghostSecondary"
            onClick={() => {
              if (clearValue !== undefined) {
                setDraft(clearValue);
                onChange(clearValue);
              }
              close();
            }}
          >
            {_t('Clear all')}
          </Button>
        </Popover.Footer>
      </Popover>
    </React.Fragment>
  );
}

export default SortSingleSelect;
